import {computed, ref} from "vue";
import {NotificationTemplate, NotificationTemplateTag} from "@/notification-template/interfaces";
import {notificationTemplateApi} from "@/container";
import {NotificationTemplateIndexParams} from "@/core/http/resources/NotificationTemplateResource";
import useSwr from "@/core/composables/swr/useSwr";
import {filterEmptyKeys} from "@/lib/utils";

export interface TemplateSection {
    tag: NotificationTemplateTag | null;
    templates: NotificationTemplate[];
}

const BASE_PARAMS: NotificationTemplateIndexParams = {
    types: ['rcs', 'rcs_flow'],
    targetable_types: ['program', 'laboratory', 'none'],
};

export const useRcsTemplates = () => {
    const selectedTag = ref<NotificationTemplateTag | null>(null);
    const refreshing = ref(false);

    const {data: tags, isLoading: tagsLoading} = useSwr(
        'rcs-tags',
        () => notificationTemplateApi().tags()
    );

    const templatesParams = computed<NotificationTemplateIndexParams>(() => {
        if (!selectedTag.value) {
            return BASE_PARAMS;
        }

        return {...BASE_PARAMS, ...buildTagParams(selectedTag.value)};
    });

    const templatesKey = computed(() => {
        const query = new URLSearchParams(filterEmptyKeys(templatesParams.value));

        return `notification-templates?${query.toString()}`;
    });

    const {data: allTemplates, isLoading: templatesLoading} = useSwr(
        templatesKey,
        () => notificationTemplateApi().index(templatesParams.value).finally(() => refreshing.value = false)
    );

    const sections = computed<TemplateSection[]>(() => {
        const templates = allTemplates.value ?? [];
        const tagList = tags.value ?? [];

        if (selectedTag.value) {
            return [{tag: selectedTag.value, templates}];
        }

        const genericTemplates = templates.filter(t => t.targetable_type === null);

        const tagSections = tagList
            .map(tag => ({
                tag,
                templates: templates.filter(
                    t => t.targetable_type === tag.type && t.targetable_id === tag.id
                ),
            }))
            .filter(section => section.templates.length > 0);

        const result: TemplateSection[] = [...tagSections];

        if (genericTemplates.length > 0) {
            result.push({tag: null, templates: genericTemplates});
        }

        return result;
    });

    const initialLoading = computed(() => tagsLoading.value && !tags.value && templatesLoading.value && !allTemplates.value);

    /**
     * Builds the index query params for a given tag,
     * mapping the tag type to the corresponding id param.
     */
    const buildTagParams = (tag: NotificationTemplateTag): Partial<NotificationTemplateIndexParams> => {
        if (tag.type === 'program') {
            return {program_id: tag.id};
        }

        return {laboratory_id: tag.id};
    };

    /**
     * Selects a tag — swrv reacts to the key change and refetches if not cached.
     */
    const selectTag = (tag: NotificationTemplateTag | null) => {
        refreshing.value = true;
        selectedTag.value = tag;
    };

    return {
        tags,
        selectedTag,
        sections,
        initialLoading,
        templatesLoading,
        selectTag,
        refreshing
    };
};
